import { FC, useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import {
	CustomizedLink,
	CustomizedText,
	StatusBox,
} from "../../../components/styled-componnets";
import Colors from "../../../utils/theme/color";
import { DD_MM_YYY } from "../../../utils/date";
import { useGetProjectsQuery } from "../../../../features/api/project-api-slice";
import { useGetUserDetailsHooks, useScreenSizeApplier } from "../../../hooks";
import { CustomizedCard, CustomizedDetailsBox } from "../styled-components";
import { getTextColorBasedOnProjectStatus } from "../../../utils/functions";
import { Status } from "../../../constants/statuses";

const customizedCardProps = { p: "2rem", pt: "1rem", pb: "1rem" };
const ERROR = "error";

interface CustomerDetailsTypes {
	details?: {
		name?: string;
		first_name?: string;
		last_name?: string;
		contact_mobile?: string;
		contact_email?: string;
		allocated_sales_team?: string;
		modified?: string;
		project?: string;
		status?: Status;
		source?: string;
		sales_stage?: string;
		pre_sales_owner?: string;
		custom_external_channel_partner?: string;
		lost_reasons?: { lost_reason: string }[];
		custom_rating?: string;
		custom_date?: string;
	};
	viewAllLostReasons?: () => void;
	// ✅ 4. UPDATE THE INTERFACE TO USE THE NEW PROP
	maskDetail?: boolean;
	loadingStatus?: boolean;
}

const CustomerDetails: FC<CustomerDetailsTypes> = ({
	details = {},
	viewAllLostReasons = () => { },
	// ✅ 5. DESTRUCTURE THE NEW PROP
	maskDetail = false,
	loadingStatus = false,
}) => {
	const { t } = useTranslation();
	const { mergeStyles } = useScreenSizeApplier();
	const { getUserToken, isCurrentUserGRE, isCurrentUserCP, isProjectHead, isProjectSalesHead, isProjectSalesUser } = useGetUserDetailsHooks();

	const [projectPublicUrl, setProjectPublicUrl] = useState<string>("");
	const [isLoadingContact, setIsLoadingContact] = useState(true);
	const [showPhone, setShowPhone] = useState(false);
	const [showEmail, setShowEmail] = useState(false);

	useEffect(() => {
		if (details?.contact_mobile) {
			setShowPhone(false);
			const timer = setTimeout(() => setShowPhone(true), 4000);
			return () => clearTimeout(timer);
		}
	}, [details?.contact_mobile]);

	useEffect(() => {
		if (details?.contact_email) {
			setShowEmail(false);
			const timer = setTimeout(() => setShowEmail(true), 4000);
			return () => clearTimeout(timer);
		}
	}, [details?.contact_email]);
	const maskPhoneNumber = (phone?: string) => {
		if (!phone) return "";
		if (phone.length <= 4) return phone; // fallback for very short numbers
		return "x".repeat(phone.length - 4) + phone.slice(-4);
	};

	const maskEmail = (email?: string) => {
		if (!email) return "";
		const [localPart, domain] = email.split("@");
		if (!localPart || !domain) return email;

		// keep last 3 characters of local part, mask the rest
		if (localPart.length > 3) {
			return "x".repeat(localPart.length - 3) + localPart.slice(-3) + "@" + domain;
		}
		// if local part is short, mask all but first letter
		return "x".repeat(localPart.length) + "@" + domain;
	};


	const {
		data: getProjectListResponse,
		isSuccess: isGetProjectListSuccess,
		isError: isGetProjectListFailed,
	} = useGetProjectsQuery({});

	const returnProjectUrlById = useCallback(
		(projectList: { name: string; public_url: string }[]) =>
			projectList.filter((item) => item?.name === details?.project),
		[details.project]
	);

	useEffect(() => {
		if (isGetProjectListSuccess) {
			if (getProjectListResponse?.message?.length) {
				setProjectPublicUrl(
					returnProjectUrlById(getProjectListResponse?.message)[0]?.public_url
				);
			} else if (!Array.isArray(getProjectListResponse?.message)) {
				toast(getProjectListResponse?.message, {
					type: ERROR,
				});
				setProjectPublicUrl("");
			}
		} else if (isGetProjectListFailed) {
			toast(t("error_messages.server_error"), {
				type: ERROR,
			});
			setProjectPublicUrl("");
		}
	}, [
		getProjectListResponse,
		isGetProjectListSuccess,
		isGetProjectListFailed,
		t,
		returnProjectUrlById,
	]);

	const renderKeyAndValue = ({
		key = "",
		value = "",
		isLink = false,
		link = "",
		Icon = null,
	}: {
		key: string;
		value?: string;
		isLink?: boolean;
		link?: string;
		Icon?: any;
	}) => (
		<Box sx={{ display: "flex" }}>
			<CustomizedText
				fontSize={18}
				fontColor={Colors.greyColor}
				fontFamily={"Lato-Regular"}
				sx={{ mr: 0.5 }}
			>
				{`${key}:`}
			</CustomizedText>
			{isLink ? (
				value ? (
					<CustomizedLink
						{...(link && {
							target: "_blank",
							href: link,
						})}
						sx={{ display: "flex", alignItems: "center" }}
					>
						{value}
						<Icon sx={{ fontSize: 16, ml: 0.3 }} />
					</CustomizedLink>
				) : null
			) : value ? (
				<CustomizedText fontSize={18} fontFamily={"Lato-Regular"}>
					{`${value}`}
				</CustomizedText>
			) : null}
		</Box>
	);

	const renderBasicDetails = () => (
		<>
			{details?.first_name && (
				<CustomizedText fontSize={32} gutterBottom>
					{`${details?.first_name} ${details?.last_name ? details?.last_name : ""
						}`}
				</CustomizedText>
			)}
			{details?.status && (
				<StatusBox sx={{ width: "20%", mb: 1 }}>
					<CustomizedText
						fontSize={16}
						fontColor={getTextColorBasedOnProjectStatus(details?.status)}
					>
						{details?.status}
					</CustomizedText>
				</StatusBox>
			)}
			{details?.lost_reasons && details?.lost_reasons?.length > 0 && (
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<CustomizedText fontSize={18} font="Lato-Italic">
						{t("opportunity_details.reasons")}
					</CustomizedText>
					<CustomizedText fontSize={18} font="Lato-Italic">
						{`${details?.lost_reasons[0].lost_reason}... `}
					</CustomizedText>
					<CustomizedLink fontSize={18} onClick={viewAllLostReasons}>
						{t("opportunity_details.view_all")}
					</CustomizedLink>
				</Box>
			)}
			{details?.contact_mobile && (
				<Box sx={{ display: "flex" }}>
					<PhoneOutlinedIcon
						sx={{
							fontSize: 20,
							color: Colors.greyColor,
						}}
					/>
					{!showPhone ? (
						<CircularProgress size={12} sx={{ ml: 1 }} />
					) : (
						<CustomizedText fontSize={20} font="Lato-Regular" sx={{ ml: 1 }}>
							{isCurrentUserGRE || (isProjectSalesUser && maskDetail)
								? maskPhoneNumber(details?.contact_mobile)
								: details?.contact_mobile}
						</CustomizedText>
					)}
				</Box>
			)}
			{details?.contact_email && (
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<EmailOutlinedIcon
						sx={{
							fontSize: 20,
							color: Colors.greyColor,
						}}
					/>
					{!showEmail ? (
						<CircularProgress size={12} sx={{ ml: 1 }} />
					) : (
						<CustomizedText fontSize={20} font="Lato-Regular" sx={{ ml: 1 }}>
							{isCurrentUserGRE || (isProjectSalesUser && maskDetail)
								? maskEmail(details?.contact_email)
								: details?.contact_email}
						</CustomizedText>
					)}
				</Box>
			)}
		</>
	);


	const renderOpportunityOtherDetails = () => (
		<Box
			sx={mergeStyles({
				styles: { p: "2rem", width: "25%" },
				belowXlStyles: { pr: 0 },
				belowLargeStyles: { p: 0, pt: "1rem" },
				tabletStyles: { p: 0, pt: "1rem", width: "30%" },
			})}
		>
			{details?.sales_stage &&
				renderKeyAndValue({
					key: t("opportunity_details.sales_stage"),
					value: details?.sales_stage,
				})}
			{details?.pre_sales_owner &&
				renderKeyAndValue({
					key: t("opportunity_details.pre_sales_owner"),
					value: details?.pre_sales_owner,
				})}
			{details?.custom_rating &&
				renderKeyAndValue({
					key: "Closure Rating",
					value: details?.custom_rating,
				})}
			{details?.custom_date &&
				renderKeyAndValue({
					key: "Follow Up Date",
					value: moment(details?.custom_date).format(DD_MM_YYY),
				})}
			{renderKeyAndValue({
				key: t("opportunity_details.last_edited"),
				value: moment(details?.modified).format(DD_MM_YYY),
			})}
		</Box>
	);

	return (
		<>
			<CustomizedCard sx={customizedCardProps}>
				<CustomizedDetailsBox>{renderBasicDetails()}</CustomizedDetailsBox>
				<CustomizedDetailsBox
					sx={mergeStyles({
						styles: { p: "2rem", pr: 0 },
						belowXlStyles: { pr: 0 },
						belowLargeStyles: { p: 0, pt: "1rem" },
						tabletStyles: { p: 0, pt: "1rem" },
					})}
				>
					{renderKeyAndValue({
						key: t("opportunity_details.opportunity_id"),
						value: details?.name,
					})}
					{renderKeyAndValue({
						key: t("opportunity_details.project"),
						value: details?.project,
						isLink: true,
						link: projectPublicUrl,
						Icon: OpenInNewRoundedIcon,
					})}
					{details?.allocated_sales_team &&
						renderKeyAndValue({
							key: t("opportunity_details.agent"),
							value: details?.allocated_sales_team,
						})}
					{details?.source &&
						renderKeyAndValue({
							key: t("opportunity_details.source"),
							value: details?.source,
						})}
					{details?.source === "Channel Partner" &&
						renderKeyAndValue({
							key: t("opportunity_details.channel_partner"),
							value: details?.custom_external_channel_partner,
						})}
				</CustomizedDetailsBox>
				{renderOpportunityOtherDetails()}
			</CustomizedCard>
			<ToastContainer />
		</>
	);
};

export default CustomerDetails;